import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Skeleton,
    alpha,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ClassIcon from '@mui/icons-material/Class';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart,
} from 'recharts';
import { format, parseISO, subMonths, subWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from '../../store/axiosApi';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

// ──── Summary Card ────
interface SummaryCardProps {
    icon: React.ReactNode;
    value: string | number;
    label: string;
    gradient: string;
    loading?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon, value, label, gradient, loading }) => {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                borderRadius: 2.5,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px -6px rgba(0,0,0,0.12)',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '40%',
                    height: '100%',
                    background: `linear-gradient(135deg, transparent 40%, ${alpha(gradient, 0.04)} 100%)`,
                    pointerEvents: 'none',
                },
            }}
        >
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${gradient} 0%, ${alpha(gradient, 0.8)} 100%)`,
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 14px ${alpha(gradient, 0.35)}`,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                {loading ? (
                    <Skeleton variant="text" width={100} height={40} />
                ) : (
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            lineHeight: 1.2,
                            color: 'text.primary',
                        }}
                    >
                        {value}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        mt: 0.3,
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Paper>
    );
};

// ──── Status Chip ────
const getStatusChip = (status: number) => {
    switch (status) {
        case 0:
        case 1:
            return <Chip label="Ativa" size="small" color="success" />;
        case 2:
            return <Chip label="Cancelada" size="small" color="error" />;
        case 5:
            return <Chip label="Finalizada" size="small" color="info" />;
        default:
            return <Chip label={`Status ${status}`} size="small" />;
    }
};

// ──── Interfaces ────
interface EnrollmentRow {
    id: number;
    dhDate: string;
    PaymentCard: number;
    Status: number;
    Person_id?: number;
    personName?: string;
}

// ──── Chart Theme ────
const chartColors = {
    primary: '#1565C0',
    primaryLight: '#42A5F5',
    error: '#D32F2F',
    errorLight: '#EF5350',
    grid: '#E0E0E0',
    text: '#637381',
};

// ──── Dashboard Component ────
const Dashboard: React.FC = () => {
    const theme = useTheme();
    const [totalAlunos, setTotalAlunos] = useState<number>(0);
    const [matriculasAtivas, setMatriculasAtivas] = useState<number>(0);
    const [parcelasEmAberto, setParcelasEmAberto] = useState<string>('0');
    const [receitaMes, setReceitaMes] = useState<string>('0');
    const [loading, setLoading] = useState<boolean>(true);
    const [enrollmentChartData, setEnrollmentChartData] = useState<any[]>([]);
    const [inadimplenciaData, setInadimplenciaData] = useState<any[]>([]);
    const [recentEnrollments, setRecentEnrollments] = useState<EnrollmentRow[]>([]);
    const [chartMode, setChartMode] = useState<'month' | 'week'>('month');
    const [enrollmentsRaw, setEnrollmentsRaw] = useState<EnrollmentRow[]>([]);
    const [estatisticaAtivos, setEstatisticaAtivos] = useState<any>(null);
    const [matriculasSemanaData, setMatriculasSemanaData] = useState<any[]>([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // ═══════════════════════════════════════════════════════
    // BUSINESS LOGIC - UNCHANGED FROM ORIGINAL
    // ═══════════════════════════════════════════════════════
    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const now = new Date();
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(now.getFullYear() - 1);

            const installmentsFilter = JSON.stringify({
                where: { DueDate: { gte: oneYearAgo.toISOString() } },
                fields: { id: true, Amount: true, AmountPaid: true, TotalPayment: true, DueDate: true, DataPagto: true, PayDate: true }
            });

            const activeEnrollFilter = JSON.stringify({
                where: {
                    and: [
                        { dhDate: { lte: now.toISOString() } },
                        { dhExpireDate: { gte: now.toISOString() } }
                    ]
                },
                fields: { id: true }
            });

            const [alunosRes, installmentsRes, recentEnrollRes, activeEnrollRes, estatRes, matSemanaRes] = await Promise.all([
                axios.get('/persons/listaAlunosBase').catch(() => ({ data: [] })),
                axios.get(`/Installments?filter=${installmentsFilter}`).catch(() => ({ data: [] })),
                fetchRecentEnrollments(),
                axios.get(`/Enrollments?filter=${activeEnrollFilter}`).catch(() => ({ data: [] })),
                axios.get('/Enrollments/getEstatisticaAtivos').catch(() => ({ data: null })),
                axios.get('/Enrollments/MatriculasPorSemana').catch(() => ({ data: [] })),
            ]);

            const alunosList = Array.isArray(alunosRes.data) ? alunosRes.data : [];
            setTotalAlunos(alunosList.length);

            const activeEnrollments = Array.isArray(activeEnrollRes.data) ? activeEnrollRes.data : [];
            setMatriculasAtivas(activeEnrollments.length);

            if (estatRes.data) {
                const estatNorm = Array.isArray(estatRes.data) ? estatRes.data[0] : estatRes.data;
                setEstatisticaAtivos(estatNorm);
            }

            const matSemanaList = Array.isArray(matSemanaRes.data) ? matSemanaRes.data : [];
            setMatriculasSemanaData(
                matSemanaList.map((i: any) => ({
                    name: `${(i.data || '').toString().substring(0, 10)} (${i.semana})`,
                    matriculas: Number(i.matriculas) || 0,
                    novoAluno: Number(i.novoAluno) || 0,
                }))
            );

            const installments = Array.isArray(installmentsRes.data) ? installmentsRes.data : [];

            const totalEmAberto = installments.reduce((acc: number, inst: any) => {
                const paid = inst.AmountPaid || inst.TotalPayment || 0;
                const saldo = (inst.Amount || 0) - paid;
                return acc + Math.max(saldo, 0);
            }, 0);
            setParcelasEmAberto(formatCurrency(totalEmAberto));

            const monthStart = startOfMonth(now);
            const monthEnd = endOfMonth(now);
            const totalReceita = installments.reduce((acc: number, inst: any) => {
                const payDate = inst.DataPagto || inst.PayDate;
                if (!payDate) return acc;
                try {
                    const d = parseISO(payDate);
                    if (isWithinInterval(d, { start: monthStart, end: monthEnd })) {
                        return acc + (inst.AmountPaid || inst.TotalPayment || 0);
                    }
                } catch { /* skip invalid dates */ }
                return acc;
            }, 0);
            setReceitaMes(formatCurrency(totalReceita));

            const enrollList = Array.isArray(recentEnrollRes) ? recentEnrollRes : [];
            setEnrollmentsRaw(enrollList);
            buildEnrollmentChart(enrollList);
            buildInadimplenciaChart(installments);

            const recent = enrollList.slice(0, 5);
            setRecentEnrollments(recent);
        } catch (e) {
            console.error('Dashboard fetch error:', e);
        } finally {
            setLoading(false);
        }
    };

    const fetchRecentEnrollments = async (): Promise<EnrollmentRow[]> => {
        try {
            const now = new Date();
            const past = new Date();
            past.setDate(now.getDate() - 365);
            const filter = JSON.stringify({
                where: { dhDate: { gte: past.toISOString() } },
                order: 'dhDate DESC'
            });
            const response = await axios.get(`/Enrollments?filter=${filter}`);
            return Array.isArray(response.data) ? response.data : [];
        } catch {
            return [];
        }
    };

    const buildEnrollmentChart = (enrollments: EnrollmentRow[], mode?: 'month' | 'week') => {
        const m = mode || chartMode;
        if (m === 'week') {
            buildEnrollmentChartWeekly(enrollments);
        } else {
            buildEnrollmentChartMonthly(enrollments);
        }
    };

    const buildEnrollmentChartMonthly = (enrollments: EnrollmentRow[]) => {
        const months: any[] = [];
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
            const monthDate = subMonths(now, i);
            const mStart = startOfMonth(monthDate);
            const mEnd = endOfMonth(monthDate);
            const label = format(monthDate, 'MMM/yy', { locale: ptBR });
            const count = enrollments.filter((e) => {
                try {
                    const d = parseISO(e.dhDate);
                    return isWithinInterval(d, { start: mStart, end: mEnd });
                } catch {
                    return false;
                }
            }).length;
            months.push({ name: label, matriculas: count });
        }
        setEnrollmentChartData(months);
    };

    const buildEnrollmentChartWeekly = (enrollments: EnrollmentRow[]) => {
        const weeks: any[] = [];
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
            const weekDate = subWeeks(now, i);
            const wStart = startOfWeek(weekDate, { weekStartsOn: 1 });
            const wEnd = endOfWeek(weekDate, { weekStartsOn: 1 });
            const label = format(wStart, 'dd/MM', { locale: ptBR });
            const count = enrollments.filter((e) => {
                try {
                    const d = parseISO(e.dhDate);
                    return isWithinInterval(d, { start: wStart, end: wEnd });
                } catch {
                    return false;
                }
            }).length;
            weeks.push({ name: label, matriculas: count });
        }
        setEnrollmentChartData(weeks);
    };

    const handleChartModeChange = (_: any, newMode: 'month' | 'week' | null) => {
        if (!newMode) return;
        setChartMode(newMode);
        buildEnrollmentChart(enrollmentsRaw, newMode);
    };

    const buildInadimplenciaChart = (installments: any[]) => {
        const now = new Date();
        const byMonth: Record<string, { name: string; valor: number }> = {};

        for (let i = 5; i >= 0; i--) {
            const monthDate = subMonths(now, i);
            const key = format(monthDate, 'yyyy-MM');
            byMonth[key] = { name: format(monthDate, 'MMM/yy', { locale: ptBR }), valor: 0 };
        }

        installments.forEach((inst: any) => {
            if (!inst.DueDate) return;
            try {
                const dueDate = parseISO(inst.DueDate);
                if (dueDate >= now) return;
                const paid = inst.AmountPaid || inst.TotalPayment || 0;
                const saldo = (inst.Amount || 0) - paid;
                if (saldo <= 0) return;

                const key = format(dueDate, 'yyyy-MM');
                if (byMonth[key]) {
                    byMonth[key].valor += saldo;
                }
            } catch { /* skip invalid dates */ }
        });

        const chartData = Object.entries(byMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, v]) => v);

        setInadimplenciaData(chartData);
    };
    // ═══════════════════════════════════════════════════════
    // END BUSINESS LOGIC
    // ═══════════════════════════════════════════════════════

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3.5 }}>
                <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Visao geral do sistema educacional
                </Typography>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        icon={<PeopleIcon sx={{ color: 'white', fontSize: 28 }} />}
                        value={totalAlunos}
                        label="Total de Alunos"
                        gradient="#1565C0"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        icon={<SchoolIcon sx={{ color: 'white', fontSize: 28 }} />}
                        value={matriculasAtivas}
                        label="Matriculas Ativas"
                        gradient="#2E7D32"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        icon={<ReceiptLongIcon sx={{ color: 'white', fontSize: 28 }} />}
                        value={parcelasEmAberto}
                        label="Parcelas em Aberto"
                        gradient="#ED6C02"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        icon={<AttachMoneyIcon sx={{ color: 'white', fontSize: 28 }} />}
                        value={receitaMes}
                        label="Receita do Mes"
                        gradient="#00897B"
                        loading={loading}
                    />
                </Grid>
            </Grid>

            {/* Estatísticas Acadêmicas */}
            {estatisticaAtivos && (
                <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            icon={<GroupIcon sx={{ color: 'white', fontSize: 28 }} />}
                            value={estatisticaAtivos.MatriculasAtivas ?? 0}
                            label="Alunos ativos (corrente)"
                            gradient="#1B5E20"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            icon={<GroupAddIcon sx={{ color: 'white', fontSize: 28 }} />}
                            value={estatisticaAtivos.MatriculasAtivasComFuturo ?? 0}
                            label="Alunos ativos + futuro"
                            gradient="#388E3C"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            icon={<SchoolIcon sx={{ color: 'white', fontSize: 28 }} />}
                            value={estatisticaAtivos.MatriculasEmDisciplinasAtivo ?? 0}
                            label="Matrículas em Disciplinas Ativo"
                            gradient="#5E35B1"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            icon={<MenuBookIcon sx={{ color: 'white', fontSize: 28 }} />}
                            value={estatisticaAtivos.MatriculasEmDisciplinasComFuturo ?? 0}
                            label="Matrículas Disc. c/ Futuro"
                            gradient="#7B1FA2"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            icon={<ClassIcon sx={{ color: 'white', fontSize: 28 }} />}
                            value={estatisticaAtivos.MatriculasDiscAtivasComFuturo ?? 0}
                            label="Disc. Ativas c/ Futuro"
                            gradient="#0277BD"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            icon={<ClassIcon sx={{ color: 'white', fontSize: 28 }} />}
                            value={estatisticaAtivos.MatriculasDiscAtivas ?? 0}
                            label="Disciplinas Ativas"
                            gradient="#00695C"
                            loading={loading}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Charts */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                <Grid item xs={12} md={7}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2.5,
                            height: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="h6" fontWeight={600} fontSize="0.95rem">
                                    {chartMode === 'month' ? 'Matrículas nos últimos 12 meses' : 'Matrículas nas últimas 12 semanas'}
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                value={chartMode}
                                exclusive
                                onChange={handleChartModeChange}
                                size="small"
                            >
                                <ToggleButton value="month" sx={{ fontSize: '0.7rem', px: 1.5, py: 0.3 }}>Mês</ToggleButton>
                                <ToggleButton value="week" sx={{ fontSize: '0.7rem', px: 1.5, py: 0.3 }}>Semana</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {loading ? (
                            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                {/* @ts-ignore - recharts types incompatible with React 18 */}
                                <AreaChart data={enrollmentChartData}>
                                    <defs>
                                        <linearGradient id="colorMatriculas" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.15} />
                                            <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: chartColors.text }}
                                        axisLine={{ stroke: chartColors.grid }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 11, fill: chartColors.text }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: 'none',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                            fontSize: 13,
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="matriculas"
                                        name="Matriculas"
                                        stroke={chartColors.primary}
                                        strokeWidth={2.5}
                                        fill="url(#colorMatriculas)"
                                        dot={{ r: 4, fill: '#fff', stroke: chartColors.primary, strokeWidth: 2 }}
                                        activeDot={{ r: 6, fill: chartColors.primary }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2.5,
                            height: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <ReceiptLongIcon sx={{ color: 'error.main', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight={600} fontSize="0.95rem">
                                Inadimplencia por Mes
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                {/* @ts-ignore - recharts types incompatible with React 18 */}
                                <BarChart data={inadimplenciaData}>
                                    <defs>
                                        <linearGradient id="colorInadimplencia" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={chartColors.error} stopOpacity={0.9} />
                                            <stop offset="100%" stopColor={chartColors.errorLight} stopOpacity={0.6} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: chartColors.text }}
                                        axisLine={{ stroke: chartColors.grid }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: chartColors.text }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        formatter={(value: number) => formatCurrency(value)}
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: 'none',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                            fontSize: 13,
                                        }}
                                    />
                                    <Bar
                                        dataKey="valor"
                                        name="Inadimplencia"
                                        fill="url(#colorInadimplencia)"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={48}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Matrículas por Semana */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="h6" fontWeight={600} fontSize="0.95rem">
                                Matrículas por Semana
                            </Typography>
                        </Box>
                        {loading ? (
                            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                        ) : (
                            <ResponsiveContainer width="100%" height={320}>
                                {/* @ts-ignore - recharts types incompatible with React 18 */}
                                <LineChart data={matriculasSemanaData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 10, fill: chartColors.text }}
                                        axisLine={{ stroke: chartColors.grid }}
                                        tickLine={false}
                                        interval="preserveStartEnd"
                                        minTickGap={20}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 11, fill: chartColors.text }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: 'none',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                            fontSize: 13,
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line
                                        type="monotone"
                                        dataKey="matriculas"
                                        name="Matrículas"
                                        stroke={chartColors.primary}
                                        strokeWidth={2.5}
                                        dot={{ r: 3, fill: '#fff', stroke: chartColors.primary, strokeWidth: 2 }}
                                        activeDot={{ r: 5, fill: chartColors.primary }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="novoAluno"
                                        name="Novos Alunos"
                                        stroke="#2E7D32"
                                        strokeWidth={2.5}
                                        dot={{ r: 3, fill: '#fff', stroke: '#2E7D32', strokeWidth: 2 }}
                                        activeDot={{ r: 5, fill: '#2E7D32' }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Recent Activity */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h6" fontWeight={600} fontSize="0.95rem">
                        Matriculas Recentes
                    </Typography>
                    <Chip
                        label={`${recentEnrollments.length} registros`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={36} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Carne</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentEnrollments.length > 0 ? (
                                    recentEnrollments.map((enrollment) => (
                                        <TableRow key={enrollment.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    #{enrollment.id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {enrollment.dhDate
                                                    ? format(parseISO(enrollment.dhDate), 'dd/MM/yyyy')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>{enrollment.PaymentCard || '-'}</TableCell>
                                            <TableCell align="center">
                                                {getStatusChip(enrollment.Status)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Nenhuma matricula recente encontrada
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Legenda dos Indicadores */}
            <Paper
                elevation={0}
                sx={{
                    mt: 3.5,
                    p: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                }}
            >
                <Typography variant="h6" fontWeight={600} fontSize="0.95rem" sx={{ mb: 2 }}>
                    Legenda dos Indicadores
                </Typography>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
                    Visão Geral
                </Typography>
                <Box component="dl" sx={{ m: 0, '& dt': { fontWeight: 600, mt: 1 }, '& dd': { ml: 0, color: 'text.secondary', fontSize: '0.85rem' } }}>
                    <dt>Total de Alunos</dt>
                    <dd>Quantos alunos a escola tem cadastrados, somando todos — antigos, atuais e inativos.</dd>

                    <dt>Matrículas Ativas</dt>
                    <dd>Quantos contratos de matrícula estão dentro do prazo de validade hoje. Mostra o contrato “no ar”, mesmo que o aluno esteja de pausa entre módulos.</dd>

                    <dt>Parcelas em Aberto</dt>
                    <dd>Total em dinheiro que ainda falta receber, somando o que sobra de cada parcela não paga (ou paga só em parte) com vencimento no último ano.</dd>

                    <dt>Receita do Mês</dt>
                    <dd>Quanto a escola recebeu de fato neste mês, somando todos os pagamentos com data de pagamento dentro do mês corrente.</dd>
                </Box>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 0.5 }}>
                    Estatísticas Acadêmicas
                </Typography>
                <Box component="dl" sx={{ m: 0, '& dt': { fontWeight: 600, mt: 1 }, '& dd': { ml: 0, color: 'text.secondary', fontSize: '0.85rem' } }}>
                    <dt>Alunos ativos (corrente)</dt>
                    <dd>Quantos alunos estão de fato cursando alguma disciplina agora — ou seja, têm aula acontecendo neste período.</dd>

                    <dt>Alunos ativos + futuro</dt>
                    <dd>Quantos alunos têm aula acontecendo agora <em>ou</em> já estão matriculados em disciplinas que vão começar/continuar à frente.</dd>

                    <dt>Matrículas em Disciplinas Ativo</dt>
                    <dd>Quantas matrículas em disciplinas estão em curso. Aqui contamos cada disciplina separadamente: um aluno cursando 3 disciplinas conta como 3.</dd>

                    <dt>Matrículas Disc. c/ Futuro</dt>
                    <dd>Mesma contagem de disciplinas, mas incluindo as que ainda não terminaram — abrange as que estão rolando agora e as futuras.</dd>

                    <dt>Disc. Ativas c/ Futuro / Disciplinas Ativas</dt>
                    <dd>Indicadores em preparação. Hoje aparecem zerados porque o sistema ainda não calcula esses valores. Serão ativados em breve.</dd>
                </Box>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 0.5 }}>
                    Gráficos
                </Typography>
                <Box component="dl" sx={{ m: 0, '& dt': { fontWeight: 600, mt: 1 }, '& dd': { ml: 0, color: 'text.secondary', fontSize: '0.85rem' } }}>
                    <dt>Matrículas nos últimos 12 meses / 12 semanas</dt>
                    <dd>Quantas matrículas novas entraram a cada mês (ou cada semana, se você trocar o botão). Útil para ver a tendência de crescimento da escola.</dd>

                    <dt>Inadimplência por Mês</dt>
                    <dd>Quanto dinheiro está em atraso, separado pelo mês em que a parcela deveria ter sido paga (últimos 6 meses).</dd>

                    <dt>Matrículas por Semana</dt>
                    <dd>Histórico semanal desde 2019. A linha <strong>Matrículas</strong> mostra quantas matrículas foram feitas naquela semana. A linha <strong>Novos Alunos</strong> mostra, dentro dessas, quantas eram a primeira matrícula da pessoa na escola.</dd>
                </Box>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 0.5 }}>
                    Tabela
                </Typography>
                <Box component="dl" sx={{ m: 0, '& dt': { fontWeight: 600, mt: 1 }, '& dd': { ml: 0, color: 'text.secondary', fontSize: '0.85rem' } }}>
                    <dt>Matrículas Recentes</dt>
                    <dd>As 5 últimas matrículas feitas no ano. A coluna Status indica se a matrícula está Ativa, Cancelada ou Finalizada.</dd>
                </Box>

                <Typography variant="caption" sx={{ display: 'block', mt: 2.5, color: 'text.disabled', fontStyle: 'italic' }}>
                    Em resumo: <strong>Matrículas Ativas</strong> conta contratos dentro do prazo. <strong>Alunos Ativos</strong> conta quem está realmente em aula agora. Um aluno em pausa entre módulos aparece no primeiro, mas não no segundo.
                </Typography>
            </Paper>
        </Box>
    );
};

export default Dashboard;
